import * as React from 'react';
import { Check, X, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { StatusBadge } from './status-badge';
import { cn } from '../../lib/utils';

export interface ApprovalCardProps {
  id: string;
  type: string; // 'leave' | 'attendance' | 'travel' | 'hr-letter'
  requester: { name: string; avatar?: string; department?: string };
  title: string;
  subtitle?: string;
  status: 'pending' | 'approved' | 'rejected' | 'revise';
  requestDate: string;
  details?: Record<string, string>;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onViewDetails?: (id: string) => void;
  className?: string;
}

// Generate initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Get color for avatar background
function getAvatarColor(name: string): string {
  const colors = [
    'bg-[var(--color-primary)]',
    'bg-[var(--color-success)]',
    'bg-[var(--color-warning)]',
    'bg-[var(--color-destructive)]',
    'bg-[var(--color-secondary)]',
  ];
  const hash = name.charCodeAt(0) + name.charCodeAt(name.length - 1);
  return colors[hash % colors.length];
}

// Get type badge color
function getTypeBadgeVariant(type: string): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline' {
  const typeVariantMap: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline'> = {
    leave: 'primary',
    attendance: 'secondary',
    travel: 'warning',
    'hr-letter': 'default',
  };
  return (typeVariantMap[type.toLowerCase()] || 'secondary') as any;
}

export const ApprovalCard = React.forwardRef<HTMLDivElement, ApprovalCardProps>(
  (
    {
      id,
      type,
      requester,
      title,
      subtitle,
      status,
      requestDate,
      details,
      onApprove,
      onReject,
      onViewDetails,
      className,
    },
    ref,
  ) => {
    const avatarColor = getAvatarColor(requester.name);
    const initials = getInitials(requester.name);
    const isPending = status === 'pending';

    return (
      <Card ref={ref} className={cn('relative overflow-hidden', className)}>
        {/* Status badge in top-right */}
        <div className="absolute top-4 right-4">
          <StatusBadge status={status} variant="pill" size="sm" />
        </div>

        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              {/* Requester avatar */}
              <div className="flex-shrink-0">
                {requester.avatar ? (
                  <img
                    src={requester.avatar}
                    alt={requester.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full font-semibold text-white text-sm',
                      avatarColor,
                    )}
                  >
                    {initials}
                  </div>
                )}
              </div>

              {/* Requester info and type badge */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-[var(--color-foreground)]">
                    {requester.name}
                  </p>
                  <Badge variant={getTypeBadgeVariant(type)} className="text-xs">
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Badge>
                </div>
                {requester.department && (
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {requester.department}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Title and subtitle */}
          <div>
            <p className="font-medium text-[var(--color-foreground)]">{title}</p>
            {subtitle && (
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {subtitle}
              </p>
            )}
          </div>

          {/* Request date */}
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Requested on {requestDate}
          </p>

          {/* Details table */}
          {details && Object.keys(details).length > 0 && (
            <div className="space-y-2 rounded-lg bg-[var(--color-muted)]/50 p-3">
              {Object.entries(details).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="font-medium text-[var(--color-muted-foreground)]">
                    {key}:
                  </span>
                  <span className="text-[var(--color-foreground)]">{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            {isPending && onApprove && (
              <Button
                variant="default"
                size="sm"
                onClick={() => onApprove(id)}
                className="flex-1"
              >
                <Check className="mr-2 h-4 w-4" />
                Approve
              </Button>
            )}
            {isPending && onReject && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onReject(id)}
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                Reject
              </Button>
            )}
            {onViewDetails && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails(id)}
                className={isPending ? '' : 'w-full'}
              >
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  },
);

ApprovalCard.displayName = 'ApprovalCard';
